/**
 * SEO content for each category landing page.
 * Each entry drives /[slug] routes and their meta, intro text, FAQ, and related links.
 */

export interface CategoryFaq {
  q: string;
  a: string;
}

export interface CategoryHighlight {
  icon: string;
  title: string;
  text: string;
}

export interface CategoryPageData {
  /** URL slug — e.g. "bowling-paris" → route /bowling-paris */
  slug: string;
  /** Matches the category slug in the DB / activityService */
  categoryId: string;
  /** Displayed H1 */
  h1: string;
  /** <title> tag */
  metaTitle: string;
  /** meta description */
  metaDescription: string;
  /** Intro paragraph(s) shown above activity cards */
  intro: string[];
  /** 3 trust/feature highlights */
  highlights: CategoryHighlight[];
  /** 6-8 activity-specific FAQs */
  faq: CategoryFaq[];
  /** categoryId values of 3 related categories */
  relatedCategories: string[];
}

export const categoryPages: CategoryPageData[] = [
  {
    slug: 'escape-game-paris',
    categoryId: 'escape-game',
    h1: 'Escape Game à Paris : Réservez en ligne',
    metaTitle: 'Escape Game à Paris : Réservez votre session | FullBook',
    metaDescription:
      'Réservez votre escape game à Paris en quelques clics. Les meilleures salles de Paris et Île-de-France, disponibles 7j/7. Paiement sécurisé, confirmation instantanée.',
    intro: [
      "L'escape game à Paris est l'une des sorties les plus populaires pour les groupes d'amis, les team buildings, les anniversaires ou les soirées en famille. Le principe est simple : enfermés dans une salle thématique, vous disposez généralement de 60 à 90 minutes pour résoudre des énigmes, déchiffrer des codes et trouver la sortie.",
      "Sur FullBook, vous avez accès aux meilleures salles d'escape game en Île-de-France. Prix à partir de 15€ par personne, pour des groupes de 2 à 10 participants. Comparez les thèmes, les niveaux de difficulté et les disponibilités en temps réel, et réservez en quelques clics.",
    ],
    highlights: [
      {
        icon: '🔐',
        title: 'Sélection des meilleures salles',
        text: 'Chaque salle partenaire est vérifiée pour sa qualité, ses équipements et ses avis joueurs.',
      },
      {
        icon: '📅',
        title: 'Disponibilités en temps réel',
        text: 'Consultez les créneaux disponibles et réservez instantanément, même le jour même.',
      },
      {
        icon: '💳',
        title: 'Paiement 100% sécurisé',
        text: 'Paiement en ligne via Stripe. Confirmation immédiate par email.',
      },
    ],
    faq: [
      {
        q: "Quel est l'âge minimum pour participer à un escape game à Paris ?",
        a: "La plupart des salles d'escape game à Paris accueillent les participants à partir de 12 ans, accompagnés d'un adulte. Certaines salles proposent des scénarios adaptés aux enfants dès 8 ans. Vérifiez les conditions de chaque établissement avant de réserver.",
      },
      {
        q: 'Combien de personnes faut-il pour un escape game ?',
        a: "La plupart des escape games accueillent entre 2 et 8 participants par session. La taille idéale d'un groupe est généralement de 3 à 5 personnes pour une bonne dynamique. Des sessions privatisées pour des groupes plus importants sont souvent disponibles sur demande.",
      },
      {
        q: "Combien de temps dure une session d'escape game ?",
        a: 'Une session dure généralement entre 60 et 90 minutes, auxquelles il faut ajouter 15 à 20 minutes pour le briefing et le débriefing. Prévoyez environ 2 heures au total pour votre sortie.',
      },
      {
        q: 'Peut-on réserver un escape game pour un anniversaire ou un EVG à Paris ?',
        a: "Absolument ! L'escape game est l'une des activités les plus populaires pour les anniversaires adultes, EVG, EVJF et team buildings. Plusieurs salles proposent des formules privatisées avec des options festives. Contactez directement l'établissement après réservation pour les détails.",
      },
      {
        q: "Faut-il avoir de l'expérience pour faire un escape game ?",
        a: "Non, aucune expérience n'est nécessaire. Les escape games ont des niveaux de difficulté variés, des salles « débutants » aux scénarios les plus complexes. Le staff est toujours disponible pour donner des indices si vous bloquez.",
      },
      {
        q: "Peut-on annuler sa réservation d'escape game ?",
        a: 'Les conditions d\'annulation dépendent de l\'établissement et sont indiquées lors de la réservation. FullBook vous permet de consulter la politique d\'annulation avant de payer.',
      },
    ],
    relatedCategories: ['laser-game', 'bowling', 'karting'],
  },

  {
    slug: 'bowling-paris',
    categoryId: 'bowling',
    h1: 'Bowling à Paris : Réservez votre piste en ligne',
    metaTitle: 'Bowling à Paris : Réservez votre piste | FullBook',
    metaDescription:
      'Bowling à Paris : réservez votre piste en ligne dans les meilleures salles d\'Île-de-France. Disponible 7j/7, paiement sécurisé. À partir de 5€ par partie.',
    intro: [
      "Le bowling à Paris est la sortie idéale pour une soirée entre amis, un anniversaire décontracté ou une sortie en famille. Paris et sa région comptent de nombreuses salles modernes, équipées de pistes éclairées, de bars et de restaurants pour prolonger la soirée.",
      "Avec FullBook, réservez votre piste de bowling en Île-de-France en quelques clics. Consultez les disponibilités en temps réel, comparez les établissements et bénéficiez d'une confirmation instantanée. Des chaussures de bowling sont fournies dans toutes les salles partenaires.",
    ],
    highlights: [
      {
        icon: '🎳',
        title: 'Pistes disponibles 7j/7',
        text: 'Réservez votre créneau en soirée, le week-end ou la semaine selon vos disponibilités.',
      },
      {
        icon: '👟',
        title: 'Chaussures incluses',
        text: 'Dans toutes nos salles partenaires, les chaussures de bowling sont fournies à la réservation.',
      },
      {
        icon: '🎂',
        title: 'Idéal pour les groupes',
        text: 'Anniversaires, soirées en entreprise, sorties en famille : le bowling convient à toutes les occasions.',
      },
    ],
    faq: [
      {
        q: 'Quel est le prix moyen d\'une partie de bowling à Paris ?',
        a: 'Le prix d\'une partie de bowling à Paris varie généralement entre 5€ et 12€ par personne selon la salle et le créneau horaire. Les soirées et week-ends sont souvent un peu plus chers. Consultez les tarifs en temps réel sur FullBook.',
      },
      {
        q: 'Faut-il apporter ses propres chaussures pour jouer au bowling ?',
        a: 'Non, les chaussures de bowling sont obligatoires pour protéger les pistes, mais elles sont systématiquement fournies et incluses dans le tarif dans toutes les salles partenaires FullBook.',
      },
      {
        q: 'Peut-on réserver une piste de bowling pour un anniversaire à Paris ?',
        a: "Oui, la plupart des salles de bowling proposent des options de réservation pour des anniversaires et des groupes. Certaines salles offrent même des packages anniversaire incluant boissons et gâteau. Contactez l'établissement après réservation pour les options festives.",
      },
      {
        q: 'Peut-on réserver une piste de bowling le jour même ?',
        a: 'Oui, sous réserve de disponibilité. FullBook affiche les créneaux disponibles en temps réel. Nous vous conseillons néanmoins de réserver à l\'avance, surtout le vendredi soir et le week-end.',
      },
      {
        q: 'Le bowling est-il accessible aux personnes à mobilité réduite ?',
        a: 'La majorité des salles de bowling partenaires FullBook sont accessibles aux personnes à mobilité réduite. Certaines disposent de rampes d\'aide au lancer adaptées. Consultez les informations d\'accessibilité de chaque établissement.',
      },
      {
        q: 'À partir de quel âge peut-on jouer au bowling ?',
        a: "Il n'y a pas d'âge minimum pour le bowling. Les enfants dès 3-4 ans peuvent jouer, souvent avec des bumpers (barrières) pour faciliter le jeu. Une supervision adulte est recommandée pour les plus jeunes.",
      },
    ],
    relatedCategories: ['laser-game', 'escape-game', 'karting'],
  },

  {
    slug: 'karting-paris',
    categoryId: 'karting',
    h1: 'Karting à Paris : Réservez votre session',
    metaTitle: 'Karting à Paris : Réservez votre session indoor | FullBook',
    metaDescription:
      'Karting indoor à Paris : réservez votre course en ligne. Circuits couverts en Île-de-France, karts électriques, pour tous niveaux. Paiement sécurisé, confirmation instantanée.',
    intro: [
      "Le karting indoor à Paris offre des sensations de course accessibles à tous, quelle que soit la météo. Sur des circuits couverts en Île-de-France, vous prenez place dans des karts électriques silencieux et performants pour des sessions intenses et sécurisées.",
      "Avec FullBook, réservez votre session de karting à Paris en quelques clics. Les circuits partenaires accueillent débutants comme pilotes expérimentés, et proposent des formules adaptées aux groupes d'amis, sorties entre collègues et team buildings.",
    ],
    highlights: [
      {
        icon: '🏎️',
        title: 'Karts électriques haute performance',
        text: 'Des karts modernes, puissants et silencieux pour des sensations authentiques en intérieur.',
      },
      {
        icon: '🏆',
        title: 'Chronométrage en temps réel',
        text: 'Suivez vos performances au tour par tour et comparez-vous à votre groupe.',
      },
      {
        icon: '👥',
        title: 'Idéal pour les groupes',
        text: 'Team building, EVG, anniversaire : le karting rassemble et crée des souvenirs.',
      },
    ],
    faq: [
      {
        q: "Quel est l'âge minimum pour faire du karting indoor à Paris ?",
        a: "L'âge minimum pour le karting indoor est généralement de 10 à 14 ans selon les circuits, et une taille minimum d'environ 1m40 est souvent requise. Certains circuits proposent des karts enfants adaptés dès 7 ans. Consultez les conditions de chaque établissement.",
      },
      {
        q: "Le karting indoor est-il dangereux ?",
        a: "Le karting indoor est une activité sécurisée. Les karts électriques sont moins puissants que les karts thermiques et les circuits sont équipés de protections latérales. Des équipements de sécurité (casque, combinaison) sont fournis par les établissements.",
      },
      {
        q: "Faut-il avoir le permis de conduire pour faire du karting ?",
        a: "Non, le permis de conduire n'est pas nécessaire pour faire du karting indoor. Aucune expérience préalable n'est requise, les circuits accueillent tous les niveaux.",
      },
      {
        q: "Combien de temps dure une session de karting ?",
        a: "Une session de karting dure généralement entre 10 et 20 minutes sur la piste. Le temps total de la visite (briefing, équipement, attente entre sessions) est généralement d'1h30 à 2h.",
      },
      {
        q: "Peut-on privatiser un circuit de karting pour un événement d'entreprise ?",
        a: "Oui, la plupart des circuits partenaires proposent des formules de privatisation pour les team buildings et événements d'entreprise. Contactez directement l'établissement après réservation pour les devis groupe.",
      },
      {
        q: "Quelle tenue porter pour faire du karting indoor ?",
        a: "Portez des vêtements confortables et couvrez-vous les jambes. Les combinaisons sont fournies par les établissements. Les chaussures fermées sont obligatoires. Les tongs et sandales sont interdits.",
      },
    ],
    relatedCategories: ['escape-game', 'laser-game', 'padel'],
  },

  {
    slug: 'laser-game-paris',
    categoryId: 'laser-game',
    h1: 'Laser Game à Paris : Réservez en ligne',
    metaTitle: 'Laser Game à Paris : Réservez votre session | FullBook',
    metaDescription:
      'Laser game à Paris : réservez votre partie en ligne dans les meilleures arènes d\'Île-de-France. À partir de 8€, groupes à partir de 2 joueurs. Confirmation immédiate.',
    intro: [
      "Le laser game est une activité incontournable pour les groupes d'amis, les anniversaires et les sorties entre collègues. Dans une arène plongée dans le noir avec effets lumineux et musique, deux équipes s'affrontent en cherchant à toucher les capteurs adverses avec leur pistolet laser.",
      "Avec FullBook, réservez votre session de laser game à Paris en quelques clics. Les arènes partenaires d'Île-de-France proposent des sessions de 15 à 20 minutes, pour des groupes de 2 à 40 personnes, disponibles 7 jours sur 7.",
    ],
    highlights: [
      {
        icon: '🔫',
        title: 'Arènes immersives',
        text: 'Des terrains labyrinthiques, des effets lumineux et une ambiance sonore pour une expérience unique.',
      },
      {
        icon: '📊',
        title: 'Tableau des scores',
        text: 'À la fin de chaque partie, consultez votre tableau des scores détaillé avec vos statistiques.',
      },
      {
        icon: '🎈',
        title: 'Anniversaires & groupes',
        text: 'Formules spéciales anniversaire disponibles dans plusieurs établissements partenaires.',
      },
    ],
    faq: [
      {
        q: "Quel est l'âge minimum pour le laser game ?",
        a: "La plupart des arènes de laser game à Paris acceptent les enfants à partir de 6-7 ans. L'activité est totalement sécurisée : les lasers utilisés sont inoffensifs (classe 1). Un adulte accompagnant est recommandé pour les enfants en dessous de 10 ans.",
      },
      {
        q: "Combien de personnes faut-il minimum pour jouer au laser game ?",
        a: "Le laser game peut se jouer à partir de 2 personnes. Pour une expérience optimale, un groupe de 6 à 20 joueurs est idéal, car il permet de former des équipes équilibrées. Des sessions ouvertes au public (avec d'autres joueurs) sont aussi disponibles.",
      },
      {
        q: "Le laser game est-il dangereux pour les yeux ?",
        a: "Non. Les pistolets laser utilisés dans les arènes commerciales sont équipés de lasers de classe 1, totalement inoffensifs pour les yeux. Aucun équipement de protection particulier n'est nécessaire.",
      },
      {
        q: "Combien de temps dure une partie de laser game ?",
        a: "Une partie de laser game dure généralement de 15 à 20 minutes dans l'arène. En comptant l'équipement, le briefing et le débriefing, prévoyez environ 45 minutes à 1 heure pour votre visite.",
      },
      {
        q: "Peut-on faire plusieurs parties de laser game à la suite ?",
        a: "Oui, la plupart des arènes proposent des formules multi-parties à tarif réduit. C'est recommandé si vous venez pour la première fois, le temps de maîtriser les règles et la stratégie.",
      },
      {
        q: "Peut-on réserver un laser game pour un anniversaire d'enfant ?",
        a: "Absolument ! Le laser game est l'une des activités les plus appréciées pour les anniversaires d'enfants dès 7 ans. De nombreux établissements proposent des formules anniversaire avec privatisation de l'arène.",
      },
    ],
    relatedCategories: ['escape-game', 'bowling', 'paintball-indoor'],
  },

  {
    slug: 'padel-paris',
    categoryId: 'padel',
    h1: 'Padel à Paris : Réservez votre terrain en ligne',
    metaTitle: 'Padel à Paris : Réservez votre terrain | FullBook',
    metaDescription:
      'Réservez votre terrain de padel à Paris en ligne. Courts couverts et en plein air en Île-de-France, disponibles 7j/7. Idéal pour débutants et confirmés.',
    intro: [
      "Le padel est l'un des sports les plus populaires en France, et Paris ne fait pas exception. Mi-tennis, mi-squash, le padel se joue en double sur un court fermé de 20m x 10m. Sport accessible dès le premier cours, il séduit aussi bien les débutants que les joueurs expérimentés.",
      "Avec FullBook, réservez votre terrain de padel à Paris et en Île-de-France en temps réel. Nos clubs partenaires proposent des courts couverts et en plein air, disponibles le soir et le week-end. Raquettes disponibles à la location dans la plupart des clubs.",
    ],
    highlights: [
      {
        icon: '🎾',
        title: 'Courts premium',
        text: 'Des terrains de padel homologués, bien entretenus, avec sol en gazon synthétique de qualité.',
      },
      {
        icon: '🌧️',
        title: 'Couverts & en plein air',
        text: 'Courts couverts disponibles toute l\'année, même par mauvais temps.',
      },
      {
        icon: '🆕',
        title: 'Idéal pour débuter',
        text: 'Le padel s\'apprend en 30 minutes. Aucune expérience n\'est requise pour réserver.',
      },
    ],
    faq: [
      {
        q: "Peut-on jouer au padel sans jamais en avoir fait ?",
        a: "Absolument. Le padel est réputé pour être facile à prendre en main. En 30 minutes, vous maîtrisez les règles de base et pouvez jouer une partie. Certains clubs proposent des cours d'initiation pour débuter dans les meilleures conditions.",
      },
      {
        q: "Combien de personnes jouent au padel ?",
        a: "Le padel se joue exclusivement en double, soit 4 joueurs par court. Si vous êtes 2 ou 3, vous pouvez contacter les clubs pour trouver des partenaires, ou réserver un cours particulier.",
      },
      {
        q: "Quelle tenue faut-il pour jouer au padel ?",
        a: "Des chaussures de sport à semelles adaptées (chaussures de tennis ou multisports) sont recommandées. Évitez les chaussures de running ou de running à crampons. Tenez-vous à des vêtements de sport confortables.",
      },
      {
        q: "Faut-il apporter sa raquette de padel ?",
        a: "Non, la plupart des clubs partenaires FullBook proposent la location de raquettes sur place. Pensez à le préciser lors de votre arrivée si vous souhaitez en louer une.",
      },
      {
        q: "Combien de temps dure un match de padel ?",
        a: "Un match de padel dure généralement 1h à 1h30 selon le format (nombre de sets). Les réservations de terrain se font généralement par tranche d'une heure.",
      },
      {
        q: "Le padel est-il une activité valable pour un team building ?",
        a: "Oui, le padel est excellent pour les team buildings car il favorise la communication, la stratégie et le dépassement de soi en équipe. Plusieurs clubs proposent des formules tournoi pour les groupes.",
      },
    ],
    relatedCategories: ['foot-indoor', 'karting', 'escape-game'],
  },

  {
    slug: 'foot-indoor-paris',
    categoryId: 'foot-indoor',
    h1: 'Foot Indoor à Paris : Réservez votre terrain',
    metaTitle: 'Foot Indoor à Paris : Réservez votre terrain | FullBook',
    metaDescription:
      'Foot indoor à Paris : réservez votre terrain en ligne. Terrains synthétiques couverts en Île-de-France, disponibles 7j/7. Réservation instantanée.',
    intro: [
      "Le foot indoor à Paris est la solution parfaite pour jouer au football toute l'année, quelle que soit la météo. Sur des terrains synthétiques couverts de taille réduite (futsal, 5v5, 7v7), vous profitez de l'intensité du football à une échelle plus dynamique et accessible.",
      "Avec FullBook, réservez votre terrain de foot indoor en Île-de-France en quelques clics. Nos complexes partenaires proposent des terrains bien entretenus, équipés de vestiaires et de douches, disponibles en soirée et le week-end.",
    ],
    highlights: [
      {
        icon: '⚽',
        title: 'Terrains synthétiques couverts',
        text: 'Jouez au football à Paris toute l\'année, sur des terrains de qualité quelles que soient les conditions météo.',
      },
      {
        icon: '🚿',
        title: 'Vestiaires & douches',
        text: 'La plupart de nos complexes partenaires sont équipés de vestiaires et de douches.',
      },
      {
        icon: '📱',
        title: 'Réservation instantanée',
        text: 'Consultez les créneaux disponibles et réservez en temps réel, sans attente.',
      },
    ],
    faq: [
      {
        q: "Combien de joueurs faut-il pour réserver un terrain de foot indoor ?",
        a: "Cela dépend du format de terrain choisi : 5v5 (10 joueurs), 7v7 (14 joueurs). Certaines structures proposent des matchs ouverts pour les groupes incomplets. Vérifiez les options disponibles sur la fiche de chaque établissement.",
      },
      {
        q: "Faut-il apporter des crampons pour jouer au foot indoor ?",
        a: "Non. Pour le foot indoor sur terrain synthétique, des chaussures de sport à semelles lisses ou des chaussures de futsal sont recommandées. Les crampons vissés sont généralement interdits pour protéger le revêtement.",
      },
      {
        q: "Peut-on réserver un terrain de foot indoor pour un tournoi entre entreprises ?",
        a: "Oui, la plupart des complexes partenaires FullBook proposent des formules pour les tournois et événements d'entreprise. Contactez directement l'établissement pour les devis.",
      },
      {
        q: "Les terrains de foot indoor sont-ils disponibles le week-end ?",
        a: "Oui, la majorité de nos partenaires proposent des créneaux 7 jours sur 7, y compris le samedi et le dimanche. Réservez à l'avance pour le week-end, les créneaux partent vite.",
      },
    ],
    relatedCategories: ['padel', 'bowling', 'laser-game'],
  },

  {
    slug: 'trampoline-paris',
    categoryId: 'trampoline-park',
    h1: 'Trampoline Park à Paris : Réservez en ligne',
    metaTitle: 'Trampoline Park à Paris : Réservez | FullBook',
    metaDescription:
      'Trampoline park à Paris : réservez votre session en ligne. Parcs de trampoline en Île-de-France pour enfants et adultes. Paiement sécurisé, confirmation instantanée.',
    intro: [
      "Les trampoline parks à Paris sont des espaces de jeu et de sport immenses, où trampolines, mousses, murs d'escalade et zones acrobatiques se combinent pour une expérience unique. Accessible à tous les âges, le trampoline park est une sortie ludique, sportive et défoulante.",
      "Avec FullBook, réservez votre session de trampoline à Paris et en Île-de-France en quelques clics. Nos parcs partenaires proposent des créneaux adaptés aux enfants, aux adolescents et aux adultes, ainsi que des formules anniversaire.",
    ],
    highlights: [
      {
        icon: '🤸',
        title: 'Pour tous les âges',
        text: 'Des zones adaptées aux enfants dès 3 ans et des espaces freestyle pour les adultes.',
      },
      {
        icon: '🎂',
        title: 'Formules anniversaire',
        text: 'Privatisation de zones et packages anniversaire disponibles dans nos parcs partenaires.',
      },
      {
        icon: '🏋️',
        title: 'Sport & fun à la fois',
        text: 'Une heure de trampoline brûle autant de calories qu\'une heure de jogging.',
      },
    ],
    faq: [
      {
        q: "À partir de quel âge peut-on aller dans un trampoline park ?",
        a: "La plupart des trampoline parks à Paris accueillent les enfants dès 3 ans dans des zones dédiées aux petits. Pour les zones avancées et acrobatiques, une taille minimum (environ 1m20) est souvent requise.",
      },
      {
        q: "Faut-il des chaussures spéciales pour le trampoline park ?",
        a: "Oui, des chaussettes antidérapantes sont obligatoires dans tous les trampoline parks pour des raisons de sécurité et d'hygiène. Vous pouvez en acheter ou en louer sur place.",
      },
      {
        q: "Combien de temps dure une session de trampoline ?",
        a: "Les sessions durent généralement 1 heure ou 2 heures. C'est amplement suffisant pour profiter de toutes les zones du parc.",
      },
      {
        q: "Le trampoline park est-il dangereux ?",
        a: "Le trampoline park est une activité encadrée et sécurisée. Des animateurs sont présents pour veiller au respect des règles de sécurité. Il est conseillé de ne pas faire de figures acrobatiques sans expérience et de suivre les consignes des moniteurs.",
      },
      {
        q: "Peut-on privatiser un trampoline park pour un anniversaire d'enfant ?",
        a: "Oui, la privatisation partielle ou totale est disponible dans la plupart de nos parcs partenaires. Ces formules incluent généralement une zone dédiée et parfois un espace pour le goûter d'anniversaire.",
      },
    ],
    relatedCategories: ['bowling', 'laser-game', 'escape-game'],
  },

  {
    slug: 'vr-arcade-paris',
    categoryId: 'vr-arcade',
    h1: 'VR Arcade à Paris : Réservez votre expérience',
    metaTitle: 'VR Arcade à Paris : Réservez votre expérience VR | FullBook',
    metaDescription:
      'VR Arcade à Paris : réservez votre expérience de réalité virtuelle en ligne. Jeux VR immersifs à Paris et en Île-de-France. Pour tous les niveaux.',
    intro: [
      "La réalité virtuelle (VR) révolutionne le divertissement à Paris. Les salles de VR arcade vous plongent dans des univers immersifs (aventure, horreur, sport, voyage) avec des équipements de pointe (casques VR haute résolution, contrôleurs haptiques).",
      "Avec FullBook, réservez votre session de VR arcade à Paris en quelques clics. Que vous soyez novice ou passionné de réalité virtuelle, nos salles partenaires proposent des expériences adaptées à tous les profils.",
    ],
    highlights: [
      {
        icon: '🥽',
        title: 'Équipements dernière génération',
        text: 'Casques VR haute résolution et contrôleurs haptiques pour une immersion totale.',
      },
      {
        icon: '🎮',
        title: 'Des dizaines d\'univers',
        text: 'Explorez des mondes virtuels : aventure, sport, horreur, voyage dans l\'espace…',
      },
      {
        icon: '🧑‍🤝‍🧑',
        title: 'Multi-joueurs disponible',
        text: 'Certaines expériences permettent de jouer en équipe dans le même espace virtuel.',
      },
    ],
    faq: [
      {
        q: "Faut-il de l'expérience pour faire de la VR arcade ?",
        a: "Non, aucune expérience préalable n'est nécessaire. Le staff vous guide et vous équipe avant la session. Les salles proposent des expériences pour tous les niveaux, des débutants complets aux joueurs avancés.",
      },
      {
        q: "La réalité virtuelle peut-elle provoquer des malaises ?",
        a: "Certaines personnes peuvent ressentir une légère sensation de vertige (motion sickness) lors des premières expériences VR, surtout dans les jeux avec mouvements rapides. Les salles partenaires proposent généralement des expériences adaptées aux débutants pour éviter cela.",
      },
      {
        q: "À partir de quel âge peut-on faire de la VR ?",
        a: "L'âge minimum recommandé pour la VR est généralement de 12 à 14 ans. Les casques VR ne sont pas conçus pour les yeux en développement des très jeunes enfants. Vérifiez les conditions de chaque établissement.",
      },
      {
        q: "Combien de temps dure une session de VR arcade ?",
        a: "Les sessions varient de 30 minutes à 2 heures selon la formule choisie. Pour une première expérience, une session d'1 heure est généralement idéale.",
      },
    ],
    relatedCategories: ['escape-game', 'laser-game', 'karting'],
  },

  {
    slug: 'quiz-room-paris',
    categoryId: 'Quiz-room',
    h1: 'Quiz Room à Paris : Réservez votre session',
    metaTitle: 'Quiz Room à Paris : Réservez en ligne | FullBook',
    metaDescription:
      'Quiz Room à Paris : réservez votre session de quiz en ligne. Soirées quiz culturelles, musicales et thématiques en Île-de-France. Pour groupes d\'amis, anniversaires et team buildings.',
    intro: [
      "Le Quiz Room est la sortie idéale pour tester vos connaissances en groupe dans une ambiance conviviale et compétitive. Questions de culture générale, musique, cinéma, histoire… Seul ou en équipe, chaque session est une nouvelle aventure intellectuelle.",
      "Avec FullBook, réservez votre session de Quiz Room à Paris en quelques clics. Nos salles partenaires accueillent des groupes de 2 à 20 personnes et proposent des formules adaptées aux anniversaires, sorties entre amis et team buildings.",
    ],
    highlights: [
      {
        icon: '🧩',
        title: 'Des centaines de questions',
        text: 'Thèmes variés : culture générale, musique, cinéma, sport, histoire et bien plus.',
      },
      {
        icon: '🏆',
        title: 'Ambiance compétitive',
        text: 'Classement en temps réel, buzzer électronique et animation assurée par des hôtes expérimentés.',
      },
      {
        icon: '🎉',
        title: 'Idéal pour les groupes',
        text: 'Anniversaires, EVG, EVJF, team buildings : le Quiz Room convient à toutes les occasions.',
      },
    ],
    faq: [
      {
        q: "Combien de personnes peut-on être pour un Quiz Room ?",
        a: "La plupart des Quiz Rooms accueillent des groupes de 2 à 20 personnes. Pour les équipes plus importantes, des formules privatisées sont souvent disponibles sur demande.",
      },
      {
        q: "Faut-il avoir de bonnes connaissances générales pour participer ?",
        a: "Non ! Les Quiz Rooms proposent des thèmes variés et des niveaux adaptés à tous les profils. L'esprit d'équipe et le fun priment sur l'érudition.",
      },
      {
        q: "Combien de temps dure une session de Quiz Room ?",
        a: "Une session dure généralement entre 1 heure et 1h30, selon le nombre de manches et le format choisi.",
      },
      {
        q: "Peut-on réserver un Quiz Room pour un anniversaire ?",
        a: "Absolument ! Le Quiz Room est une activité très appréciée pour les anniversaires adultes. Plusieurs établissements proposent des formules spéciales avec privatisation de la salle.",
      },
      {
        q: "Les Quiz Rooms sont-ils accessibles aux enfants ?",
        a: "Certains Quiz Rooms proposent des sessions adaptées aux enfants ou à la famille. Vérifiez l'âge minimum recommandé sur la fiche de chaque établissement.",
      },
    ],
    relatedCategories: ['escape-game', 'laser-game', 'bowling'],
  },

  {
    slug: 'activites-insolites-paris',
    categoryId: 'autre',
    h1: 'Activités insolites à Paris : Réservez en ligne',
    metaTitle: 'Activités insolites à Paris : Expériences uniques | FullBook',
    metaDescription:
      'Activités insolites à Paris : réservez des expériences uniques et originales en Île-de-France. Sorties originales pour groupes d\'amis, anniversaires et team buildings.',
    intro: [
      "Vous cherchez une sortie qui sort de l'ordinaire ? Les activités insolites à Paris vous proposent des expériences uniques et mémorables, loin des sentiers battus. Chasse aux vampires, jeux immersifs, aventures scénarisées… laissez-vous surprendre.",
      "Avec FullBook, réservez vos activités insolites à Paris en quelques clics. Nos partenaires proposent des expériences originales pour tous les groupes, idéales pour les anniversaires, soirées entre amis et team buildings.",
    ],
    highlights: [
      {
        icon: '✨',
        title: 'Expériences uniques',
        text: 'Des activités qu\'on ne trouve nulle part ailleurs, sélectionnées pour leur originalité.',
      },
      {
        icon: '🎭',
        title: 'Immersion totale',
        text: 'Scénarios immersifs et animateurs passionnés pour une expérience inoubliable.',
      },
      {
        icon: '🎈',
        title: 'Pour tous les groupes',
        text: 'Anniversaires, EVG, EVJF, team buildings : des formules adaptées à chaque occasion.',
      },
    ],
    faq: [
      {
        q: "Qu'est-ce qu'une activité insolite à Paris ?",
        a: "Une activité insolite est une expérience sortant de l'ordinaire : jeux immersifs, aventures scénarisées, chasses au trésor originales… Tout ce qui sort des activités classiques et crée des souvenirs uniques.",
      },
      {
        q: "Ces activités sont-elles adaptées aux enfants ?",
        a: "Cela dépend de l'activité. Certaines sont ouvertes à tous dès 10 ans, d'autres sont réservées aux adultes. Consultez l'âge minimum sur la fiche de chaque expérience.",
      },
      {
        q: "Peut-on réserver une activité insolite pour un anniversaire ?",
        a: "Oui, les activités insolites sont parfaites pour les anniversaires ! L'originalité de l'expérience garantit un moment mémorable pour tout le groupe.",
      },
      {
        q: "Combien de personnes peuvent participer ?",
        a: "La capacité varie selon l'activité, généralement de 2 à 15 personnes. Des formules de groupe sont disponibles pour les équipes plus importantes.",
      },
    ],
    relatedCategories: ['escape-game', 'vr-arcade', 'laser-game'],
  },

  {
    slug: 'billard-paris',
    categoryId: 'billard',
    h1: 'Billard à Paris : Réservez votre table en ligne',
    metaTitle: 'Billard à Paris : Réservez votre table | FullBook',
    metaDescription:
      "Réservez votre table de billard à Paris en ligne. Billard américain, snooker, carambole : les meilleures salles d'Île-de-France disponibles 7j/7. Confirmation instantanée.",
    intro: [
      "Le billard à Paris est une activité de loisirs intemporelle, accessible à tous les niveaux. Dans les salles partenaires FullBook, vous trouverez des tables de billard américain (pool), de snooker et de carambole dans des établissements modernes et bien équipés.",
      "Avec FullBook, réservez votre table de billard en Île-de-France en quelques clics. Que vous soyez débutant curieux ou joueur confirmé, nos établissements partenaires vous accueillent 7 jours sur 7, en soirée et le week-end.",
    ],
    highlights: [
      {
        icon: '🎱',
        title: 'Plusieurs disciplines',
        text: 'Billard américain, snooker, carambole : choisissez votre discipline préférée dans nos salles partenaires.',
      },
      {
        icon: '📅',
        title: 'Tables disponibles 7j/7',
        text: 'Consultez les disponibilités en temps réel et réservez instantanément, même le jour même.',
      },
      {
        icon: '👥',
        title: 'Idéal en groupe',
        text: 'Le billard est parfait pour une soirée entre amis ou une sortie en couple. Plusieurs tables disponibles en simultané.',
      },
    ],
    faq: [
      {
        q: "Faut-il avoir de l'expérience pour jouer au billard ?",
        a: "Non, le billard est accessible à tous les niveaux, du débutant complet au joueur confirmé. Le staff est disponible pour vous expliquer les règles si c'est votre première fois.",
      },
      {
        q: 'Quels types de billard sont disponibles à Paris ?',
        a: "Les salles parisiennes proposent principalement le billard américain (pool), le plus pratiqué en loisirs. Certaines salles proposent également le snooker (grande table à 15 boules rouges) ou le billard carambole (sans poches). Vérifiez les types disponibles sur la fiche de chaque établissement.",
      },
      {
        q: "Combien de temps dure une session de billard ?",
        a: "Les tables se réservent généralement à l'heure. Une heure est largement suffisante pour une ou deux parties en loisirs. Pour les joueurs plus sérieux, des formules de 2h ou 3h sont disponibles.",
      },
      {
        q: "Faut-il apporter sa propre queue de billard ?",
        a: "Non, les salles de billard fournissent toutes les queues et accessoires nécessaires (craie, triangle, etc.). Si vous avez votre propre matériel, vous pouvez bien entendu l'apporter.",
      },
      {
        q: "Quel est le prix d'une table de billard à Paris ?",
        a: "Le prix d'une table de billard à Paris varie généralement entre 8 € et 20 € de l'heure selon la salle, le type de table et le créneau horaire. Les soirées du vendredi et samedi sont souvent un peu plus chères.",
      },
      {
        q: "Peut-on organiser un tournoi de billard pour un groupe ?",
        a: "Oui, plusieurs salles partenaires proposent des formules de tournoi pour les groupes, team buildings et soirées d'entreprise. Contactez directement l'établissement après réservation pour les options groupe.",
      },
    ],
    relatedCategories: ['bowling', 'laser-game', 'escape-game'],
  },

  {
    slug: 'paintball-paris',
    categoryId: 'paintball-indoor',
    h1: 'Paintball Indoor à Paris : Réservez votre session',
    metaTitle: 'Paintball Indoor à Paris : Réservez | FullBook',
    metaDescription:
      "Paintball indoor à Paris : réservez votre session en ligne. Arènes couvertes en Île-de-France, équipements fournis, pour tous niveaux. Idéal pour EVG, anniversaires et groupes.",
    intro: [
      "Le paintball indoor à Paris offre toutes les sensations du paintball classique dans un environnement couvert, accessible toute l'année quelle que soit la météo. Sur des terrains intérieurs avec décors thématiques, vous vivrez des affrontements tactiques intenses avec des équipements professionnels.",
      "Avec FullBook, réservez votre session de paintball indoor en Île-de-France en quelques clics. Tous les équipements sont fournis sur place : combinaison, masque et marqueurs. Nos arènes partenaires accueillent des groupes à partir de 6 joueurs pour des sessions privatisées.",
    ],
    highlights: [
      {
        icon: '🔫',
        title: 'Équipements professionnels fournis',
        text: 'Combinaison, masque homologué et marqueur fournis à chaque session. Aucun matériel personnel requis.',
      },
      {
        icon: '🏟️',
        title: 'Terrains couverts thématiques',
        text: 'Des arènes indoor immersives avec obstacles, décors et zones tactiques pour des parties épiques.',
      },
      {
        icon: '🎉',
        title: 'Parfait pour les événements',
        text: "EVG, anniversaires adultes, team building : le paintball indoor est l'activité fédératrice par excellence.",
      },
    ],
    faq: [
      {
        q: "Le paintball indoor est-il douloureux ?",
        a: "Le paintball indoor utilise des billes de peinture qui font un léger impact à l'arrivée, comparable à une pichenette. La combinaison et le masque fournis protègent les zones sensibles. La plupart des joueurs décrivent la sensation comme légèrement surprenante mais pas vraiment douloureuse.",
      },
      {
        q: "À partir de quel âge peut-on faire du paintball indoor à Paris ?",
        a: "L'âge minimum pour le paintball indoor est généralement de 12 à 14 ans selon les arènes. Des équipements adaptés aux jeunes joueurs sont disponibles dans certains établissements. Vérifiez les conditions d'âge sur la fiche de l'établissement avant de réserver.",
      },
      {
        q: "Combien de personnes faut-il pour une session de paintball ?",
        a: "Une session de paintball indoor nécessite généralement un minimum de 6 joueurs pour former deux équipes équilibrées. La plupart des arènes peuvent accueillir jusqu'à 30 joueurs simultanément pour les grands groupes.",
      },
      {
        q: "Quelle tenue porter pour le paintball indoor ?",
        a: "Les combinaisons de protection sont fournies sur place et s'enfilent par-dessus vos vêtements normaux. Portez des vêtements confortables que vous n'avez pas peur de salir légèrement (la peinture est lavable). Des chaussures de sport fermées sont obligatoires.",
      },
      {
        q: "Combien de billes de peinture reçoit-on par session ?",
        a: "Le nombre de billes inclus dans le tarif varie selon la formule choisie. En général, les formules incluent entre 100 et 500 billes par joueur. Des billes supplémentaires peuvent être achetées sur place.",
      },
      {
        q: "Peut-on privatiser une arène de paintball pour un EVG ou un anniversaire ?",
        a: "Oui, la privatisation est l'une des formules les plus populaires pour les EVG, EVJF et anniversaires adultes. Contactez l'établissement après réservation pour les options de privatisation et les packages événement.",
      },
    ],
    relatedCategories: ['laser-game', 'escape-game', 'karting'],
  },
];

/** Map categoryId → slug for internal linking */
export const categorySlugMap: Record<string, string> = Object.fromEntries(
  categoryPages.map((p) => [p.categoryId, p.slug])
);

/** Get category page data by slug */
export function getCategoryPageBySlug(slug: string): CategoryPageData | undefined {
  return categoryPages.find((p) => p.slug === slug);
}

/** Get category page data by categoryId */
export function getCategoryPageByCategoryId(categoryId: string): CategoryPageData | undefined {
  return categoryPages.find((p) => p.categoryId === categoryId);
}
